/* eslint no-console: off */
import { TimeRange } from 'app/entities/dashboard/timeRange';
import { useGraphDelta } from 'app/shared/util/use-graph-delta';
import { eachDayOfInterval } from 'date-fns';
import React, { useMemo } from 'react';

import { DataQuery } from 'app/entities/dashboard/utils/utils';
import { getLayerConfigs, isDataRenderable } from './influx/config';
import { Plot } from '@influxdata/giraffe';
import { GeoChart } from './influx/geo';
import { Spinner } from 'reactstrap';

type HistogramProps = {
  dataQuery?: DataQuery;
  updateOnce?: boolean;
  timeRange: TimeRange;
  type: string;
  colorScheme?: string | null;
  widgetOption?: string | null;
  preview?: boolean;
};

export const FluxChart = ({ type, dataQuery, timeRange="-7d", colorScheme, updateOnce = true, widgetOption, preview }: HistogramProps) => {
  const { data, rawData, isLoading, isFetched } = useGraphDelta({
    dataQuery,
    timeRange,
    updateOnce,
    enabled: true,
    displayType: type,
  });
  const isAnomalyChart = dataQuery?.operation === 'ANOMALY';

  const xTicksDynamic= useMemo(() => {
    switch (timeRange) {
      case '-7d':
        return eachDayOfInterval({ start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), end: new Date() }).map(d => d.getTime());
      case '-14d':
        return eachDayOfInterval({ start: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), end: new Date() }, { step: 2 }).map(d =>
          d.getTime(),
        );
      case '-30d':
        return eachDayOfInterval({ start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), end: new Date() }, { step: 5 }).map(d =>
          d.getTime(),
        );
      case '-90d':
        return eachDayOfInterval({ start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), end: new Date() }, { step: 10 }).map(d =>
          d.getTime(),
        );
      default:
        return undefined;
    }
  }, [timeRange]);

  const xTicks = useMemo(() => {
    if (!data?.table || type !== 'histogram') return undefined;

    // Replace '_value' with your actual x column name
    const values = data.table.getColumn('_value');
    if (!values) return undefined;

    const min = Math.min(...values);
    const max = Math.max(...values);

    const step = (max - min) / 9; // 9 ticks → 10 intervals
    return Array.from({ length: 10 }, (_, i) => +(min + step * i).toFixed(1));
  }, [data, type]);

  const layers = getLayerConfigs({
    graphType: type,
    colorScheme,
    table: data?.table,
    isAnomalyChart,
  });

  const config = useMemo(() => {
    return {
      table: data?.table,
      fluxResponse: rawData,
      showAxes: !['single stat', 'map'].includes(type),
      xScale: 'linear',
      yScale: 'linear',
      gridColor: '#c0c0c0',
      gridOpacity: 0.3,
      style: {
        border: 'dashboard',
      },
      xTicks,
      staticLegend:
        type === 'line'
          ? {
              backgroundColor: 'transparent',
              border: '1px solid #3B658A',
              colorizeRows: true,
              font: '12px sans-serif',
              heightRatio: 0.5,
              hide: false,
              orientationThreshold: 20,
            }
          : undefined,
      layers,
    };
  }, [data, type, rawData, xTicks, layers]);

  return (
    <div style={{ width: '100%', height: '100%', padding: '10px', display: 'flex', position: 'relative', overflow: 'hidden' }}>
      {/* show loading when in preview format and in widget layout for first time. */}
      {(isLoading && preview) || (!isFetched && isLoading && !preview) ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Spinner color="#168884" />
        </div>
      ) : (
        <>
          {
            <>
              {data && data.table.length > 0 && isDataRenderable(data?.table, type) ? (
                /* rerendering single component for all types causes null exception, hence enforcing separate components as per graph types */
                <>
                  {['single stat', 'histogram', 'bar'].includes(type) && <Plot config={config} />}
                  {['line'].includes(type) && <Plot config={config} />}
                  {['table', 'gauge'].includes(type) && <Plot config={config} />}
                  {['map'].includes(type) && <GeoChart data={data} metrics={dataQuery.metric} preview={preview} />}
                </>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                  <span>Try adjusting filters — no renderable data found</span>
                </div>
              )}
            </>
          }
        </>
      )}
    </div>
  );
};
